<template>
  <div class="hero-search-result" v-if="userArray">
    <div
      class="hero-search-result__empty-text card"
      v-if="userArray.length === 0"
    >
      No users matching your query
    </div>

    <div v-if="userSelectableArray.length > 0">
      <div class="row">
        <div class="col col-xs-12 col-md-4">
          <div class="hero-search-result__list card">
            <div class="hero-search-result__list-header">
              <div class="hero-search-result__list-title">
                Search results
              </div>

              <el-button type="primary" @click="toggleSelectAll">
                {{ isAllSelected ? 'Reset select' : 'Select all' }}
              </el-button>
            </div>

            <div
              :class="[
                'hero-search-result__hero',
                { 'hero-search-result__hero--active': isUserActive(user) },
              ]"
              v-for="user in userSelectableArray"
              :key="user.userId"
            >
              <div
                class="hero-search-result__hero-info"
                @click="setActiveUser(user)"
              >
                <el-avatar class="hero-search-result__hero-avatar" :size="32">
                  <user-avatar :userId="user.userId" :alt="user.name" />
                </el-avatar>

                <span class="hero-search-result__hero-name">
                  {{ user.name }}
                </span>
              </div>

              <span class="hero-search-result__hero-check">
                <el-checkbox v-model="user.isSelected"></el-checkbox>
              </span>
            </div>
          </div>

          <el-button
            class="hero-search-result__button"
            type="primary"
            @click="handleInviteUsers"
            :disabled="!isUsersSelected"
          >
            Invite
          </el-button>
        </div>

        <div class="col col-xs-12 col-md-8">
          <div class="hero-search-result__preview" v-if="activeUser">
            <public-c-v-preview :user-id="activeUser.userId" />
          </div>

          <div class="hero-search-result__preview-empty card" v-else>
            Click on a user from search results to view their CV
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="isInviteConfirmationVisible"
      width="40%"
      title="Invite users to team"
    >
      <p>
        Do you want to send an invitation to these people:
      </p>

      <div class="hero-search-result__hero-list">
        <div class="row">
          <div
            class="col col-xs-12 col-md-6"
            v-for="user in selectedUsers"
            :key="user.userId"
          >
            <div class="hero-search-result__user-preview">
              <el-avatar
                class="hero-search-result__user-preview-avatar"
                :size="40"
              >
              <user-avatar :userId="user.userId" :alt="user.name" />
              </el-avatar>
              <span class="hero-search-result__user-preview-name">
                {{ user.name }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
      <span class="dialog-footer">
        <el-button @click="isInviteConfirmationVisible = false">
          Cancel
        </el-button>
        <el-button
          type="primary"
          @click="requestInviteUsers"
          :disabled="isButtonDisabled('invite-users')"
        >
          Invite
        </el-button>
      </span>
    </template>
    </el-dialog>
  </div>
</template>
<script>
import PublicCVPreview from '@/components/profile/PublicCVPreview.vue';
import { addTeammates } from '@/api/team.js';
import { showMessage } from '@/utils/messages.js';
import { parseErrorsByFields } from '@/utils/index.js';
import { disableButton } from '@/mixins/disableButton.js';
import UserAvatar from '@/components/profile/UserAvatar.vue';

export default {
  name: 'SearchResult',
  props: {
    total: {
      type: Number,
      required: false,
    },
    userArray: {
      type: Array,
      required: false,
    },
    teamId: {
      type: Number,
      required: true,
    },
  },
  mixins: [disableButton],
  components: {
    PublicCVPreview,
    UserAvatar,
  },
  data() {
    return {
      userSelectableArray: [],
      activeUser: null,
      isInviteConfirmationVisible: false,
    };
  },
  computed: {
    isAllSelected() {
      return this.userSelectableArray.every((user) => user.isSelected);
    },
    isUsersSelected() {
      return this.userSelectableArray.some((user) => user.isSelected);
    },
    selectedUsers() {
      return this.userSelectableArray.filter((user) => user.isSelected);
    },
    inviteFormData() {
      const { teamId, selectedUsers } = this;
      const userIds = selectedUsers.map((user) => user.userId);

      return {
        teamId,
        userIds,
      };
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    async requestInviteUsers() {
      this.disableButton('invite-users');
      const { inviteFormData } = this;
      const { success } = await addTeammates(inviteFormData);
      this.enableButton('invite-users');

      if (success) {
        this.isInviteConfirmationVisible = false;

        this.showMessage({
          type: 'success',
          message: 'Invitations have been sent',
        });
        this.$emit('update-data');
      }
    },
    setData(userData) {
      this.userSelectableArray = userData.map((user) => {
        return {
          userId: user.userContactInfo.userId,
          name:
            user.userContactInfo.firstName +
            (user.userContactInfo.lastName === null ? '' : ' ' + user.userContactInfo.lastName),
          isSelected: false,
        };
      });
    },
    toggleSelectAll() {
      const { isAllSelected } = this;

      this.userSelectableArray.forEach((user) => {
        user.isSelected = !isAllSelected;
      });
    },
    setActiveUser(user) {
      this.activeUser = user;
    },
    isUserActive(user) {
      const { activeUser } = this;
      if (activeUser) {
        return activeUser.userId === user.userId;
      }
      return false;
    },
    handleInviteUsers() {
      this.isInviteConfirmationVisible = true;
    },
  },
  created() {
    const { userArray } = this;
    if (userArray) {
      this.setData(userArray);
    }
  },
  watch: {
    userArray(newVal) {
      if (newVal.length > 0) {
        this.setData(newVal);
      }
    },
  },
};
</script>
